import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import RightArrow from "../../../../Assets/Icons/RightArrow.png";
import LeftArrow from "../../../../Assets/Icons/LeftArrow.png";
import { fetchServices, deleteService } from "../../../../modules/NursignCare/ManageServices/ManageServiceThunk";
import ToggleSwitch from "../../../../widgets/ToggleSwitch/ToggleSwitch";
import ToastPopup from "../../../../widgets/Toast/toastPopup";

const ServiceGroupList = ({ searchValue, onEntrySummaryChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [clientPage, setClientPage] = useState(1);

  const { services, servicesTotalDocuments } = useSelector((state) => state.manageService);

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchServices({ page: 1, limit: 10000 }));
  }, []);

  useEffect(() => {
    setClientPage(1);
  }, [searchValue]);

  const getLocaleString = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;

    let lang = i18n.language?.toLowerCase();
    if (lang === "vietnam") lang = "vi";
    if (lang === "vn") lang = "vi";

    return val[lang] || val.en || val.vi || "";
  };

  const handleToggle = async (id, e) => {
    e.stopPropagation();
    const result = await dispatch(deleteService(id));
    if (result?.meta?.requestStatus === "fulfilled") {
      ToastPopup.success(t("serviceGroupStatusUpdated") || "Status updated successfully");
    }
  };

  const filteredItems = useMemo(() => {
    const q = (searchValue || "").trim().toLowerCase();
    return (services || [])
      .filter((item) => {
        if (!q) return true;
        const group = getLocaleString(item.title).toLowerCase();
        return group.includes(q) || (item.code || "").toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return getLocaleString(a.title).localeCompare(getLocaleString(b.title), undefined, { sensitivity: "base" });
      });
  }, [services, searchValue, i18n.language]);

  const totalClientPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const pagedItems = filteredItems.slice((clientPage - 1) * itemsPerPage, clientPage * itemsPerPage);

  useEffect(() => {
    onEntrySummaryChange?.({
      displayed: filteredItems.length,
      total: servicesTotalDocuments || (services || []).length,
    });
  }, [filteredItems.length, servicesTotalDocuments, onEntrySummaryChange]);

  const renderPagination = () => {
    const pages = [];
    if (totalClientPages <= 4) {
      for (let i = 1; i <= totalClientPages; i++) pages.push(i);
    } else if (clientPage <= 2) {
      pages.push(1, 2, { type: "ellipsis" }, totalClientPages);
    } else if (clientPage >= totalClientPages - 1) {
      pages.push(1, { type: "ellipsis" }, totalClientPages - 1, totalClientPages);
    } else {
      pages.push(1, { type: "ellipsis" }, clientPage, { type: "ellipsis" }, totalClientPages);
    }
    return pages;
  };

  return (
    <div className="nc-mr-secondary-box">
      <div className="nc-mr-price-box">
        <table className="nc-nr-price-layout">
          <thead className="nc-mr-thead">
            <tr>
              <th style={{ width: "60%" }}>{t("serviceGroup") || "Service Group"}</th>
              <th className="nc-ms-status-col" style={{ width: "20%" }}>{t("status") || "Status"}</th>
              <th className="nc-ms-action-col" style={{ width: "20%" }}>{t("action") || "Action"}</th>
            </tr>
          </thead>
          <tbody className="nc-mr-tbody">
            {pagedItems.map((item) => {
              const serviceGroup = getLocaleString(item.title) || "-";

              return (
                <tr key={item._id} className="nc-mr-table-row">
                  <td title={serviceGroup}>{serviceGroup}</td>
                  <td className="nc-ms-status-col">
                    <span className={`status-pill ${item.isActive ? "status-paid" : "status-absconded"}`}>
                      {item.isActive ? t("active") : t("inactive")}
                    </span>
                  </td>
                  <td className="nc-ms-action-col">
                    <ToggleSwitch
                      isOn={item.isActive}
                      handleToggle={(e) => handleToggle(item._id, e)}
                    />
                  </td>
                </tr>
              );
            })}
            {pagedItems.length === 0 && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                  {t("noDataAvailable")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalClientPages > 1 && (
        <div className="nc-mr-pagination-list">
          <button
            className="nc-mr-pagination-prev-list"
            onClick={() => setClientPage(Math.max(1, clientPage - 1))}
            disabled={clientPage === 1}
          >
            <img src={LeftArrow} alt="Previous" />
          </button>
          {renderPagination().map((p, i) =>
            p.type === "ellipsis" ? (
              <span key={`ellipsis-${i}`}>...</span>
            ) : (
              <button
                key={p}
                className={`nc-mr-pagination-btn-list ${clientPage === p ? "active-list" : ""}`}
                onClick={() => setClientPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="nc-mr-pagination-next-list"
            onClick={() => setClientPage(Math.min(totalClientPages, clientPage + 1))}
            disabled={clientPage === totalClientPages}
          >
            <img src={RightArrow} alt="Next" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ServiceGroupList;
